import {
  Spin,
  Empty,
  Typography,
  Button,
  Modal,
  Form,
  Input,
  Table,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined } from "@ant-design/icons";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPost from "../../hooks/useAxiosPost";
import useMultiNotification from "../../hooks/useNotification";
import { getUserType } from "../../helpers/userDetails";
import "./sitesTable.css";

interface SiteTableDataType {
  key: number;
  intPkCompanySiteID: number;
  intPkCompanyID: number;
  siteName: string;
  siteAddress: string;
  dateCreated?: string;
  enable?: number;
  mikrotiksCount?: number;
  onlineUsersCount?: number;
}

const SitesComponent = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [
    postApiData,
    apiResponse,
    apiError,
    apiLoading,
    apiMessage,
    apiErrorCount,
  ] = useAxiosPost<ResponseProp>("company-sites/all");

  const [
    createSiteData,
    createSiteResponse,
    createSiteError,
    createSiteLoading,
    createSiteMessage,
    createSiteErrorCount,
  ] = useAxiosPost<ResponseProp>("company-sites/create");

  const [allSites, setAllSites] = useState<any[]>([]);
  const postApiDataRef = useRef(postApiData);
  const hasProcessedCreateSuccess = useRef(false);
  const lastProcessedErrorCount = useRef({ api: 0, create: 0 });

  // Keep ref updated
  useEffect(() => {
    postApiDataRef.current = postApiData;
  }, [postApiData]);

  useEffect(() => {
    postApiDataRef.current({});
  }, []);

  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: any = apiResponse.responseData;
      if (Array.isArray(responseData)) {
        // Transform data for table with keys
        const sitesWithKeys = responseData.map((site: any) => ({
          ...site,
          key: site.intPkCompanySiteID,
        }));
        setAllSites(sitesWithKeys);
      } else {
        setAllSites([]);
      }
    }
  }, [apiResponse]);

  useEffect(() => {
    if (
      apiError !== null &&
      apiErrorCount !== 0 &&
      lastProcessedErrorCount.current.api !== apiErrorCount
    ) {
      lastProcessedErrorCount.current.api = apiErrorCount;
      openNotification("error", "Failed to Load Sites", apiError);
    }
  }, [apiError, apiErrorCount, openNotification]);

  // Handle create site success - only process once
  useEffect(() => {
    if (createSiteResponse !== null && !hasProcessedCreateSuccess.current) {
      hasProcessedCreateSuccess.current = true;
      openNotification("success", "Site Created", "Site created successfully");
      setIsModalOpen(false);
      form.resetFields();
      // Refresh sites list
      setTimeout(() => {
        postApiDataRef.current({});
      }, 100);
    }
  }, [createSiteResponse]);

  // Reset the flag when modal opens or createSiteResponse becomes null
  useEffect(() => {
    if (createSiteResponse === null) {
      hasProcessedCreateSuccess.current = false;
    }
  }, [createSiteResponse]);

  // Handle create site error
  useEffect(() => {
    if (
      createSiteError !== null &&
      createSiteErrorCount !== 0 &&
      lastProcessedErrorCount.current.create !== createSiteErrorCount
    ) {
      lastProcessedErrorCount.current.create = createSiteErrorCount;
      openNotification("error", "Failed to Create Site", createSiteError);
    }
  }, [createSiteError, createSiteErrorCount, openNotification]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    if (!createSiteLoading) {
      setIsModalOpen(false);
      form.resetFields();
    }
  };

  const handleCreate = (values: any) => {
    const { siteName, siteAddress } = values;
    createSiteData({
      siteName: siteName.trim(),
      siteAddress: siteAddress.trim(),
    });
  };

  const handleModalClose = () => {
    if (!createSiteLoading) {
      handleCancel();
    }
  };

  const handleRowClick = (record: SiteTableDataType) => {
    navigate(`/site-details/${record.intPkCompanySiteID}`);
  };

  const columns: ColumnsType<SiteTableDataType> = [
    {
      title: "Site Name",
      dataIndex: "siteName",
      key: "siteName",
      render: (text: string) => (
        <Typography.Text strong>{text}</Typography.Text>
      ),
    },
    {
      title: "Site Address",
      dataIndex: "siteAddress",
      key: "siteAddress",
      render: (address: string) => (
        <span style={{ wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal" }}>
          {address}
        </span>
      ),
    },
    {
      title: "Mikrotiks",
      dataIndex: "mikrotiksCount",
      key: "mikrotiksCount",
      render: (count: number) => count ?? 0,
      align: "center",
    },
    {
      title: "Online Users",
      dataIndex: "onlineUsersCount",
      key: "onlineUsersCount",
      render: (count: number) => count ?? 0,
      align: "center",
    },
    {
      title: "Site Status",
      dataIndex: "enable",
      key: "enable",
      render: (enable: number) => (
        <Typography.Text type={enable === 1 ? "success" : "secondary"}>
          {enable === 1 ? "Active" : "Inactive"}
        </Typography.Text>
      ),
    },
  ];

  const userRole = getUserType();
  const canCreateSite = userRole === 'ADMIN' ;

  return (
    <>
      {contextHolder}
      {canCreateSite && (
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={showModal}
            size="large"
          >
            Create Site
          </Button>
        </div>
      )}

      <Modal
        title="Create New Site"
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        maskClosable={!createSiteLoading}
        closable={!createSiteLoading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
          requiredMark={false}
        >
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Site Name</span>}
            name="siteName"
            rules={[
              {
                required: true,
                message: "Please enter site name",
              },
              {
                min: 1,
                message: "Site name cannot be empty",
              },
              {
                max: 100,
                message: "Site name must be less than 100 characters",
              },
              {
                whitespace: true,
                message: "Site name cannot be only spaces",
              },
            ]}
          >
            <Input
              placeholder="Enter site name"
              disabled={createSiteLoading}
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Site Address</span>}
            name="siteAddress"
            rules={[
              {
                required: true,
                message: "Please enter site address",
              },
              {
                min: 1,
                message: "Site address cannot be empty",
              },
              {
                max: 200,
                message: "Site address must be less than 200 characters",
              },
              {
                whitespace: true,
                message: "Site address cannot be only spaces",
              },
            ]}
          >
            <Input.TextArea
              placeholder="Enter site address"
              rows={3}
              disabled={createSiteLoading}
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              onClick={handleCancel}
              style={{ marginRight: "8px" }}
              disabled={createSiteLoading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={createSiteLoading}
              disabled={createSiteLoading}
            >
              {createSiteLoading ? "Creating..." : "Create Site"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {apiLoading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
          <Typography style={{ marginTop: "16px", display: "block" }}>
            Loading sites...
          </Typography>
        </div>
      ) : apiError && !apiLoading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Empty
            description={
              <Typography.Text type="danger">
                {apiError || "Failed to load sites"}
              </Typography.Text>
            }
          />
        </div>
      ) : allSites.length === 0 ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Empty description="No sites found. Create your first site!" />
        </div>
      ) : (
        <div className="table-container-custom">
          <Table
            className="antd-table-striped sites-table-clickable"
            columns={columns}
            dataSource={allSites}
            pagination={false}
            loading={apiLoading}
            rowClassName={(record: SiteTableDataType) =>
              record.enable !== undefined && Number(record.enable) === 0 ? "site-disabled" : ""
            }
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: "pointer" },
            })}
          />
        </div>
      )}
    </>
  );
};
export default SitesComponent;
